import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { piiProps } from 'src/components/DemoBlur';
import { Iconify } from '../../../../../components';
import useCustomSnackbar from '../../../../../hooks/useCustomSnackbar';
import { getLastContactChannelMeta } from '../../../../../utils/lastContact';

// Most common existing "copy" icon in this codebase (grep count across src/:
// mdi:content-copy 28, eva:copy-fill 25, eva:copy-outline 21) — standardised
// on here rather than introducing a fourth variant.
const COPY_ICON = 'mdi:content-copy';

const copyToClipboard = async (value) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
    }
    return false;
};

// One row of the popover: a label, the value (or "—" when missing), a Copy
// icon button, and an optional action button (Call for phone). Both Copy and
// the action are disabled when the value is missing.
const ContactDetailRow = ({ label, value = '', onCopy, action = null }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, minWidth: 260 }}>
        <Typography variant='caption' color='text.secondary' sx={{ width: 48, flexShrink: 0 }}>
            {label}
        </Typography>
        <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }} {...piiProps}>
            {value || '—'}
        </Typography>
        <Tooltip title={`Copy ${label.toLowerCase()}`}>
            <span>
                <IconButton size='small' onClick={onCopy} disabled={!value} aria-label={`Copy ${label.toLowerCase()}`}>
                    <Iconify icon={COPY_ICON} width={16} height={16} />
                </IconButton>
            </span>
        </Tooltip>
        {action}
    </Box>
);

ContactDetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    onCopy: PropTypes.func.isRequired,
    action: PropTypes.node,
};

// Rendered ONCE by NewLeadDataGrid.jsx (not per-cell) — see
// ContactDetailsCell.jsx's header comment for why the anchor/row state lives
// at the parent level instead of inside the cell. `anchorEl`/`row` come from
// that parent state; `onClose` clears it (Escape / outside click / native
// MUI Popover close all route through the same onClose prop).
//
// "Send email" was scoped out: the in-app composer the Contact Lead popup
// uses for its Email tab (IosEmailComposerBody, see FollowUpV2.jsx ~L1842-
// 1853) needs leadDetail/practiceId/templateId already loaded into Redux
// (followLead/leadDetail) plus its own dialog chrome and submit flow — opening
// it standalone from a table cell is well beyond a ~30-line addition. "Call"
// reuses the same plain `tel:` anchor the rest of this app already uses for
// click-to-call (MobileLeadCard.jsx:150, FollowUpV2.jsx:1476) rather than a
// JS handler — there's no shared callLead()/handleCall() function to call.
const ContactDetailsPopover = ({ anchorEl = null, row = null, onClose }) => {
    const { enqueueSnackbar } = useCustomSnackbar();
    const email = row?.email || '';
    const phone = row?.phone || '';
    const open = Boolean(anchorEl);
    const phoneMeta = getLastContactChannelMeta('PHONE');

    const handleCopy = (label, value) => async (e) => {
        e.stopPropagation();
        const copied = await copyToClipboard(value);
        if (copied) {
            enqueueSnackbar(`${label} copied`, { variant: 'success' });
        }
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            onClick={(e) => e.stopPropagation()}
        >
            <Box role='dialog' aria-label='Contact details' sx={{ p: 1.5 }}>
                <ContactDetailRow
                    label='Email'
                    value={email}
                    onCopy={handleCopy('Email', email)}
                />
                <ContactDetailRow
                    label='Phone'
                    value={phone}
                    onCopy={handleCopy('Phone', phone)}
                    action={(
                        <Button
                            component='a'
                            href={phone ? `tel:${phone}` : undefined}
                            onClick={(e) => { if (!phone) e.preventDefault(); e.stopPropagation(); }}
                            disabled={!phone}
                            size='small'
                            variant='outlined'
                            startIcon={<Iconify icon={phoneMeta.icon} width={14} height={14} />}
                        >
                            Call
                        </Button>
                    )}
                />
            </Box>
        </Popover>
    );
};

ContactDetailsPopover.propTypes = {
    anchorEl: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    row: PropTypes.shape({
        email: PropTypes.string,
        phone: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
};

export default ContactDetailsPopover;
